import React, { useRef } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { useMutation } from 'react-apollo';
import gql from 'graphql-tag';
import { get, find } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import * as successionReadinessActionCreators from '../../actions/successionReadinessActionCreators';
import { Table, TableHead, TableBody, TableRow, TableCell, Checkbox, CircularProgress, Typography } from '@material-ui/core';
import moment from 'moment';
import ActivityCard from './ActivityCards/ActivityCard';
import { yearsFromRetirementOptions } from './SuccessionReadinessBanner';
import { FormattedMessage } from 'react-intl';

const ToggleReadinessActivityMutation = gql`
  mutation ToggleReadinessActivity($id: ID!, $status: Boolean!) {
    toggleReadinessActivity(id: $id, status: $status) {
      id
      key 
      title
      text 
      score 
      completed
      category
      dueDate
      ctaTarget
      ctaText
      ctaType
    }
  }
`;

const ResetReadinessActivityMutation = gql`
  mutation ResetReadinessActivity($id: ID!) {
    resetReadinessActivity(id: $id) {
      id
    }
  }
`;


const SuccessionActivityBanner = ({classes, successionReadiness, refetchFunc, setOpen, setRoomId}) => {
  // useMutaion hook
  const [toggleReadinessActivity] = useMutation(ToggleReadinessActivityMutation);
  const [resetReadinessActivity] = useMutation(ResetReadinessActivityMutation);
  const [selectedActivity, setSelectedActivity] = React.useState(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isResetting, setIsResetting] = React.useState(false);
  const [isConfirmed, setIsConfirmed] = React.useState(false); 

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    setIsConfirmed(false);
  };

  const handleReset = async () => {
    try {
      setIsResetting(true);
      await resetReadinessActivity({
        variables: {
          id: successionReadiness.id
        }
      });
      
      await refetchFunc({});
      setRoomId(false);
      toggleModal();
      
    } catch (error) {
      console.error('Error resetting assessment:', error);
    } finally {
      setIsResetting(false);
    }
  };

  const onCardClose = () => {
    setSelectedActivity(null);
  }

  const rowClicked = (activity) => {
    setSelectedActivity(activity);
  }

  const readinessActivities = get(successionReadiness, 'readinessActivities', []);
  // Filter out helpful_resources from completion calculations
  const completableActivities = readinessActivities.filter(activity => activity.key !== 'helpful_resources');
  const completedActivities = completableActivities.filter(activity => activity.completed === true);

  return (
    <div>
      <ActivityCard isOpen={Boolean(selectedActivity)} onClose={onCardClose} activity={selectedActivity} />
      <h2> 
        To-Do List
      </h2>
      <div className={classes.header}>
        <div className={classes.completion}>
          <span>Based On Your Succession Readiness Assessment Completed On
            <br/>
            <b>
              {moment(get(successionReadiness, 'completionDate')).format("MMM, D, YYYY")}
            </b>
            &nbsp;your goal is to retire in&nbsp;
            <b>{find(yearsFromRetirementOptions, ['value', get(successionReadiness, 'yearsFromRetirement', 0) * 10] ).label}</b>.<br/>
            Below are the key tasks to reach your goal, you can click on the task to view more details.
          </span>
          <div>&nbsp;</div>
          
        </div>
        <div className={classes.scoreBox}>
          <CircularProgress
            className={classes.progress}
            variant="determinate"
            color="#dadada"
            value={100} // Convert to percentage
            size={100}
            style={{position: 'absolute', top: 0, zIndex: 1, color: '#dadada'}}
          />
          <CircularProgress
            className={classes.progress}
            variant="determinate"
            value={(completedActivities.length / completableActivities.length) * 100} // Convert to percentage
            size={100}
            sx={{
              color: (theme) =>
                theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
            }}
            style={{zIndex: 2}}
          />
          
          
          <Typography
            variant="h6"
            component="div"
            color="textSecondary"
            className={classes.percentage}
          >
            {`${completedActivities.length} of ${completableActivities.length}`}
          </Typography>
        </div>
      </div>
      <div className={classes.container}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.status}>Status</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell className={classes.dueDate}>Due Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {readinessActivities.map((item, index) => (
              <TableRow key={index}>
                <TableCell className={[classes.cell, classes.status].join(', ')}>
                  {item.key !== 'helpful_resources' ? (
                    <Checkbox
                      checked={item.completed === true}
                      onClick={() => {
                        toggleReadinessActivity({
                          variables: {
                            id: item.id,
                            status: !item.completed
                          },
                        }).then(() => {
                          // Refetch the data
                          refetchFunc({});
                        });
                      }} />
                  ) : null}
                </TableCell>
                <TableCell 
                  className={[classes.cell, classes.text].join(', ')}
                  onClick={() => rowClicked(item)}>{item.title}
                </TableCell>
                <TableCell 
                  className={[classes.cell, classes.text].join(', ')}
                  onClick={() => rowClicked(item)}>{item.category}
                </TableCell>
                <TableCell 
                  className={[classes.cell, classes.dueDate].join(', ')}
                  onClick={() => rowClicked(item)}>
                  {item.key !== 'helpful_resources' ? moment(item.dueDate).format('MMMM DD, YYYY') : 'N/A'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className={classes.buttonBox}>
        <a 
          className="btn btn-default"
          style={{cursor: 'pointer'}}
          target="_blank"
          href="/succession_readiness.pdf">Print Assessment</a>
        &nbsp;&nbsp;
        <a 
          className="btn btn-default"
          style={{cursor: 'pointer'}}
          onClick={() => {
            setRoomId(get(successionReadiness, 'conversationId'));
            setOpen(true);
          }}>Review Assessment</a>
        &nbsp;&nbsp;
        <a 
          className="btn btn-default"
          style={{cursor: 'pointer'}}
          target="_blank" 
          href="/media/contact">
            Schedule a Consultation
        </a>
        &nbsp;&nbsp;
        <button 
          className="btn btn-default"
          style={{cursor: 'pointer'}}
          onClick={toggleModal}>
          Reset
        </button>
      
       {/* Modal */}
          <div 
          className="modal" 
          style={{ 
            display: isModalOpen ? 'block' : 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-dialog" 
            style={{
              margin: '10% auto',
              width: '90%',
              maxWidth: '500px',
              backgroundColor: 'white',
              position: 'relative'
            }}
          >
            <div className="modal-content">
              <div className="modal-body">
                <span 
                  style={{
                    float: 'right',
                    cursor: 'pointer',
                    fontSize: '20px',
                    fontWeight: 'bold'
                  }}
                  onClick={toggleModal}
                >
                  X
                </span>
                
                <h2 style={{ marginBottom: '20px' }}>Reset Assessment</h2>
                <p style={{ marginBottom: '20px' }}>
                  Clicking reset will delete your assessment and any completed action items. 
                  Are you sure you want to reset?
                </p>
                
                <div style={{ marginBottom: '20px' }}>
                  <input 
                    type="checkbox" 
                    id="confirmReset"
                    checked={isConfirmed}
                    onChange={(e) => {
                      setIsConfirmed(e.target.checked);
                    }}
                  /> 
                  <label htmlFor="confirmReset" style={{ marginLeft: '10px' }}>
                    I understand this action cannot be undone
                  </label>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <button
                    id="resetSubmitButton"
                    className="btn btn-default"
                    disabled={!isConfirmed || isResetting}
                    onClick={() => {
                      handleReset();
                    }}
                    style={{ marginRight: '10px' }}
                  >
                    {isResetting ? 'Resetting...' : 'Reset Assessment'}
                  </button>
                  <button
                    className="btn btn-default"
                    onClick={toggleModal}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SuccessionActivityBanner.propTypes = {
  successionReadiness: PropTypes.array.isRequired,
};

const mapStateToProps = state => {
  return {
    successionReadiness: get(state, 'successionReadiness.successionReadiness', []),
    refetchFunc: get(state, 'successionReadiness.refetch', () => {}),
  }
};

const styles = theme => ({
  container: {
    maxWidth: '590px',
    maxHeight: '300px', // Setting max height to contain the table
    overflow: 'auto', // Enabling vertical scrolling if necessary
  },
  table: {
    width: '100%',
    tableLayout: 'fixed', // Ensures cells have equal width
    overflowWrap: 'break-word', // Wrap long words
  },
  tableBody: {
    // Remove maxHeight and overflowY from tableBody
  },
  cell: {
    margin: '4px',
    padding: '4px',
    wordWrap: 'break-word',
  },
  status: {
    width: '80px',
  },
  dueDate: {
    width: '150px',
  },
  text: {
    padding: '0px 4px 0px 0px',
  },
  scoreBox: {
    position: 'relative', 
    width: '100px', 
    height: '100px', 
    margin: '10px',
  },
  score: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    paddingRight: '30px'
  },
  completion: {
    fontSize: '16px',
    padding: 8,
  },
  buttonBox: {
    display: 'flex',
    padding: '16px 2px',
  },
  progress: {
    margin: 0,
    position: 'relative',
    width: '100px',
    height: '100px',
  },
  percentage: {
    position: 'absolute',
    top: '34%',
    left: '26%',
  },
});


export default withStyles(styles)(connect(mapStateToProps, {...successionReadinessActionCreators})(SuccessionActivityBanner));